
import { defineComponent, ref, PropType, Ref, computed, watch, ComputedRef, onMounted, nextTick } from 'vue'
import InputKin from '@/views/components/InputKin.vue'
import { ShokushuKmkTypeType, shokushuTypeType, ShokushuTypeType } from '@/repositories/keiei-joho/data'
import LoadingButton from '@/views/components/LoadingButton.vue'
import { CorporationInfoPick, FacilityInfoPick, initializeShokushuKmk, ResponseShokushuRegisterLayout, ResponseShokushuShukeiData, ShokushuKmkType } from '@/repositories/keiei-joho/shokushu/data'
import { hospitalClinicKbnType } from '@/views/corporation-info/types'
import TooltipIcon from '@/views/components/TooltipIcon.vue'

type CheckboxStateType = {
  isChecked: boolean,
  isDisabled: boolean
}

function initializeCheckboxState (items: ShokushuKmkType[]): CheckboxStateType {
  const hasNonNullKin = items.some(item => item.kin !== null)
  return {
    isChecked: hasNonNullKin,
    isDisabled: !hasNonNullKin
  }
}

export default defineComponent({
  components: { TooltipIcon, LoadingButton, InputKin },
  props: {
    corporationInfo: {
      type: Object as PropType<CorporationInfoPick>
    },
    facilityInfo: {
      type: Object as PropType<FacilityInfoPick>
    },
    shokushuItems: {
      type: Array as PropType<ShokushuKmkType[]>,
      required: true
    },
    startDate: {
      type: String,
      required: true
    },
    endDate: {
      type: String,
      required: true
    }
  },
  emits: {
    register: () => true,
    recalculate: (callback: (data: ResponseShokushuRegisterLayout) => void) => { return typeof callback === 'function' },
    copy: (callback: (data: ResponseShokushuShukeiData[]) => void) => { return typeof callback === 'function' }
  },
  setup (props, { emit }) {
    // 職種別に分けてcomputedでリアクティブに管理
    const getItems = (shokushuType: ShokushuTypeType): ShokushuKmkType[] => {
      return props.shokushuItems.filter(v => v.shokushuType === shokushuType) ?? [initializeShokushuKmk()]
    }
    const doctorItems = computed(() => getItems(shokushuTypeType.Doctor))
    const dentistItems = computed(() => getItems(shokushuTypeType.Dentist))
    const pharmacistItems = computed(() => getItems(shokushuTypeType.Pharmacist))
    const nursingStaffItems = computed(() => getItems(shokushuTypeType.NursingStaff))
    const publicHealthNurseItems = computed(() => getItems(shokushuTypeType.PublicHealthNurse))
    const midwifeItems = computed(() => getItems(shokushuTypeType.Midwife))
    const nurseItems = computed(() => getItems(shokushuTypeType.Nurse))
    const assistantNurseItems = computed(() => getItems(shokushuTypeType.AssistantNurse))
    const otherMedicalTechniciansItems = computed(() => getItems(shokushuTypeType.OtherMedicalTechnicians))
    const radiologicalTechnologistItems = computed(() => getItems(shokushuTypeType.RadiologicalTechnologist))
    const clinicalEngineerItems = computed(() => getItems(shokushuTypeType.ClinicalEngineer))
    const medicalLaboratoryTechnologistItems = computed(() => getItems(shokushuTypeType.MedicalLaboratoryTechnologist))
    const rehabilitationStaffItems = computed(() => getItems(shokushuTypeType.RehabilitationStaff))
    const physicalTherapistItems = computed(() => getItems(shokushuTypeType.PhysicalTherapist))
    const occupationalTherapistItems = computed(() => getItems(shokushuTypeType.OccupationalTherapist))
    const orthoptistItems = computed(() => getItems(shokushuTypeType.Orthoptist))
    const speechTherapistItems = computed(() => getItems(shokushuTypeType.SpeechTherapist))
    const dentalHygienistItems = computed(() => getItems(shokushuTypeType.DentalHygienist))
    const dentalTechnicianItems = computed(() => getItems(shokushuTypeType.DentalTechnician))
    const nutritionistEtcItems = computed(() => getItems(shokushuTypeType.NutritionistEtc))
    const dietitianItems = computed(() => getItems(shokushuTypeType.Dietitian))
    const nutritionistItems = computed(() => getItems(shokushuTypeType.Nutritionist))
    const cookItems = computed(() => getItems(shokushuTypeType.Cook))
    const socialWorkerItems = computed(() => getItems(shokushuTypeType.SocialWorker))
    const psychiatricSocialWorkerItems = computed(() => getItems(shokushuTypeType.PsychiatricSocialWorker))
    const nurseryTeacherItems = computed(() => getItems(shokushuTypeType.NurseryTeacher))
    const nursingAssistantItems = computed(() => getItems(shokushuTypeType.NursingAssistant))
    const officeStaffItems = computed(() => getItems(shokushuTypeType.OfficeStaff))
    const administrativeStaffItems = computed(() => getItems(shokushuTypeType.AdministrativeStaff))
    const medicalClerkItems = computed(() => getItems(shokushuTypeType.MedicalClerk))
    const medicalInformationManagerItems = computed(() => getItems(shokushuTypeType.MedicalInformationManager))
    const otherStaffItems = computed(() => getItems(shokushuTypeType.OtherStaff))

    // 職種別のチェックボックス
    const checkboxStates = ref<Record<ShokushuTypeType, CheckboxStateType>>({
      Doctor: initializeCheckboxState(doctorItems.value),
      Dentist: initializeCheckboxState(dentistItems.value),
      Pharmacist: initializeCheckboxState(pharmacistItems.value),
      NursingStaff: initializeCheckboxState(nursingStaffItems.value),
      PublicHealthNurse: initializeCheckboxState(publicHealthNurseItems.value),
      Midwife: initializeCheckboxState(midwifeItems.value),
      Nurse: initializeCheckboxState(nurseItems.value),
      AssistantNurse: initializeCheckboxState(assistantNurseItems.value),
      OtherMedicalTechnicians: initializeCheckboxState(otherMedicalTechniciansItems.value),
      RadiologicalTechnologist: initializeCheckboxState(radiologicalTechnologistItems.value),
      ClinicalEngineer: initializeCheckboxState(clinicalEngineerItems.value),
      MedicalLaboratoryTechnologist: initializeCheckboxState(medicalLaboratoryTechnologistItems.value),
      RehabilitationStaff: initializeCheckboxState(rehabilitationStaffItems.value),
      PhysicalTherapist: initializeCheckboxState(physicalTherapistItems.value),
      OccupationalTherapist: initializeCheckboxState(occupationalTherapistItems.value),
      Orthoptist: initializeCheckboxState(orthoptistItems.value),
      SpeechTherapist: initializeCheckboxState(speechTherapistItems.value),
      DentalHygienist: initializeCheckboxState(dentalHygienistItems.value),
      DentalTechnician: initializeCheckboxState(dentalTechnicianItems.value),
      NutritionistEtc: initializeCheckboxState(nutritionistEtcItems.value),
      Dietitian: initializeCheckboxState(dietitianItems.value),
      Nutritionist: initializeCheckboxState(nutritionistItems.value),
      Cook: initializeCheckboxState(cookItems.value),
      SocialWorker: initializeCheckboxState(socialWorkerItems.value),
      PsychiatricSocialWorker: initializeCheckboxState(psychiatricSocialWorkerItems.value),
      NurseryTeacher: initializeCheckboxState(nurseryTeacherItems.value),
      NursingAssistant: initializeCheckboxState(nursingAssistantItems.value),
      OfficeStaff: initializeCheckboxState(officeStaffItems.value),
      AdministrativeStaff: initializeCheckboxState(administrativeStaffItems.value),
      MedicalClerk: initializeCheckboxState(medicalClerkItems.value),
      MedicalInformationManager: initializeCheckboxState(medicalInformationManagerItems.value),
      OtherStaff: initializeCheckboxState(otherStaffItems.value)
    })

    // 職種別のチェックボックス
    const itemMap: Record<ShokushuTypeType, ComputedRef<ShokushuKmkType[]>> = {
      Doctor: doctorItems,
      Dentist: dentistItems,
      Pharmacist: pharmacistItems,
      NursingStaff: nursingStaffItems,
      PublicHealthNurse: publicHealthNurseItems,
      Midwife: midwifeItems,
      Nurse: nurseItems,
      AssistantNurse: assistantNurseItems,
      OtherMedicalTechnicians: otherMedicalTechniciansItems,
      RadiologicalTechnologist: radiologicalTechnologistItems,
      ClinicalEngineer: clinicalEngineerItems,
      MedicalLaboratoryTechnologist: medicalLaboratoryTechnologistItems,
      RehabilitationStaff: rehabilitationStaffItems,
      PhysicalTherapist: physicalTherapistItems,
      OccupationalTherapist: occupationalTherapistItems,
      Orthoptist: orthoptistItems,
      SpeechTherapist: speechTherapistItems,
      DentalHygienist: dentalHygienistItems,
      DentalTechnician: dentalTechnicianItems,
      NutritionistEtc: nutritionistEtcItems,
      Dietitian: dietitianItems,
      Nutritionist: nutritionistItems,
      Cook: cookItems,
      SocialWorker: socialWorkerItems,
      PsychiatricSocialWorker: psychiatricSocialWorkerItems,
      NurseryTeacher: nurseryTeacherItems,
      NursingAssistant: nursingAssistantItems,
      OfficeStaff: officeStaffItems,
      AdministrativeStaff: administrativeStaffItems,
      MedicalClerk: medicalClerkItems,
      MedicalInformationManager: medicalInformationManagerItems,
      OtherStaff: otherStaffItems
    }

    // 職種のチェックボックスのありなしでその行をまとめて制御
    //   チェックあり：0を入れる
    //   チェックなし：nullを入れる。（見た目は空欄になる）。またdisabled状態にする
    const checkboxWatchers = () => {
      (Object.keys(itemMap) as ShokushuTypeType[]).forEach((key) => {
        const checkboxState = checkboxStates.value[key]
        const items = itemMap[key]

        watch(() => checkboxState.isChecked, (checked) => {
          checkboxState.isDisabled = !checked
          const kin = checked ? 0 : null
          for (const item of items.value) item.kin = kin
        })
      })
    }

    // 循環参照を避けるために後ろの方でwatch式を作成
    checkboxWatchers()

    // マウント後にチェックボックス全てにtabindex="-1"を一括付与（テンプレート内で個別設定するのが冗長なため）
    onMounted(async () => {
      document.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach(el => {
        el.tabIndex = -1
      })
    })

    /**
     * 登録
     */
    const onClickedRegister = () => {
      updateCheckedShokushuNullToZero()
      emit('register')
    }

    /**
     * 再集計
     */
    const onClickedRecalculate = () => {
      emit('recalculate', async (data: ResponseShokushuRegisterLayout) => {
        await updateShokushuItem(data.kmkTypes)
      })
    }

    /**
     * 人数複写
     */
    const onClickedCopy = () => {
      emit('copy', async (data: ResponseShokushuShukeiData[]) => {
        const ninzuDataList = data.filter(item => item.isNinzuKmk)
        await updateShokushuItem(ninzuDataList)
      })
    }

    /**
     * 再集計と人数複写による表示内容更新を共通化
     */
    const updateShokushuItem = async (itemList: { kmkType: ShokushuKmkTypeType; kin: number | null }[]) => {
      // 金額が null 以外の職種リストを作成
      const inputtedShokushuTypeList: ShokushuTypeType[] = []
      itemList.filter(v => v.kin !== null).forEach(v => {
        const item = props.shokushuItems.find(item => item.kmkType === v.kmkType)
        if (item && !inputtedShokushuTypeList.includes(item.shokushuType)) {
          inputtedShokushuTypeList.push(item.shokushuType)
        }
      })

      // チェックボックスを先に更新（チェックボックスwatch発火による処理の競合を防ぐため）
      inputtedShokushuTypeList.forEach(type => {
        checkboxStates.value[type].isChecked = true
      })

      // watchが確定してから金額項目を更新
      await nextTick()
      itemList.forEach(v => {
        const shokushuItem = props.shokushuItems.find(item => item.kmkType === v.kmkType)
        if (shokushuItem) {
          shokushuItem.kin = v.kin
        }
      })
    }

    /**
     * 職種にチェックが入っている場合、その職種の中のnull項目を0に変換する
     */
    const updateCheckedShokushuNullToZero = () => {
      const checkedShokushu = Object.entries(checkboxStates.value).filter(([key, value]) => value.isChecked).map(([key]) => key as ShokushuTypeType)
      checkedShokushu.forEach(shokushu => {
        const items = itemMap[shokushu]
        items.value.forEach(item => {
          if (item.kin === null) {
            item.kin = 0
          }
        })
      })
    }

    /**
     * 「病院」か「診療所」の文字列を返す
     */
    const clinicOrHospitalLabel = computed(() => {
      return props.facilityInfo?.hospitalClinicKbn === hospitalClinicKbnType.Hospital ? '病院' : '診療所'
    })

    return {
      doctorItems,
      dentistItems,
      pharmacistItems,
      nursingStaffItems,
      publicHealthNurseItems,
      midwifeItems,
      nurseItems,
      assistantNurseItems,
      otherMedicalTechniciansItems,
      radiologicalTechnologistItems,
      clinicalEngineerItems,
      medicalLaboratoryTechnologistItems,
      rehabilitationStaffItems,
      physicalTherapistItems,
      occupationalTherapistItems,
      orthoptistItems,
      speechTherapistItems,
      dentalHygienistItems,
      dentalTechnicianItems,
      nutritionistEtcItems,
      dietitianItems,
      nutritionistItems,
      cookItems,
      socialWorkerItems,
      psychiatricSocialWorkerItems,
      nurseryTeacherItems,
      nursingAssistantItems,
      officeStaffItems,
      administrativeStaffItems,
      medicalClerkItems,
      medicalInformationManagerItems,
      otherStaffItems,

      checkboxStates,

      onClickedRegister,
      onClickedRecalculate,
      onClickedCopy,
      clinicOrHospitalLabel
    }
  }
})
